import { useMemo } from 'react'
import type { SubscriptionPrice, SubscriptionCurrency } from '@/types/api'
import { useSubscriptionPricesQuery } from './useSubscriptionPricesQuery'

/**
 * Форматирует название плана для UI. Если бэк отдал `initial_months` —
 * собираем «Подписка N {месяц/месяца/месяцев}» по правилам ru, иначе фоллбэк
 * на «Подписка {suffix}».
 */
export const formatPlanName = (plan: SubscriptionPrice): string => {
  const months = plan.initial_months
  if (typeof months === 'number' && months > 0) {
    return `Подписка ${months} ${pluralizeMonths(months)}`
  }
  return `Подписка ${plan.suffix}`
}

const CURRENCY_SYMBOL: Record<SubscriptionCurrency, string> = {
  RUB: '₽',
  EUR: '€',
}

/**
 * Цена + символ валюты: `990 ₽`, `19 €`. Неизвестная валюта — fallback на
 * ISO-код (`990 USD`), чтобы UI не падал, если бэк начнёт отдавать ещё что-то.
 */
export const formatPrice = (
  price: number,
  currency: SubscriptionCurrency | string
): string => {
  const symbol =
    (CURRENCY_SYMBOL as Record<string, string | undefined>)[currency] ?? currency
  return `${price.toLocaleString('ru-RU')} ${symbol}`
}

const pluralizeMonths = (n: number): string => {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return 'месяц'
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'месяца'
  return 'месяцев'
}

/**
 * Возвращает функцию-селектор плана по `subscription_info_id`.
 * Под капотом — справочник тарифов из `useSubscriptionPricesQuery`.
 */
export const usePlanLookup = () => {
  const { data, isLoading } = useSubscriptionPricesQuery()

  const lookup = useMemo(() => {
    const map = new Map<number, SubscriptionPrice>()
    for (const price of data?.subscriptions ?? []) {
      map.set(price.subscription_info_id, price)
    }
    return (subscriptionInfoId: number | undefined) =>
      subscriptionInfoId == null ? undefined : map.get(subscriptionInfoId)
  }, [data])

  return { lookup, isLoading }
}
